import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Modal } from 'react-native';
import { IUser } from '../../types/user';
import { IVerification, IVerificationData } from '../../types/verification';
import GenericButton from '../../components/molecules/buttons/GenericButton';
import Icon from '@mdi/react';
import { mdiCheck, mdiClose, mdiHelp } from '@mdi/js';
import { Tooltip } from '../../components/molecules/Tooltip';
import { Picker } from '@react-native-picker/picker';

interface PresTableProps {
  users: IUser[];
  verifications: IVerification[];
  verificationRequests: any[] | undefined;
  onSendVerificationRequest: (email: string, verificationId: string) => void;
  onSendGroupVerificationRequest: (connectionIds: string[], verification_id: string) => void;
}

const ITEMS_PER_PAGE = 10;

const PresTable: React.FC<PresTableProps> = ({
  users,
  verifications,
  onSendVerificationRequest,
  onSendGroupVerificationRequest,
  verificationRequests
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState<string | undefined>(undefined);
  const [selectedVerificationId, setSelectedVerificationId] = useState<string | undefined>(
    undefined
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState<IVerification | undefined>(
    undefined
  );
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [groupVerificationModalVisible, setGroupVerificationModalVisible] = useState(false);
  const [groupVerificationId, setGroupVerificationId] = useState<string | undefined>(undefined);
  const [groupSelectedVerification, setGroupSelectedVerification] = useState<
    IVerification | undefined
  >(undefined);
  const totalPages = Math.ceil(users.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentUsers = users.slice(startIndex, endIndex);

  const getLatestVerificationRequestByUser = (requests: any[]) => {
    const latestMap: Record<string, any> = {};
    requests.forEach((req) => {
      const userId = req.userName;
      if (!latestMap[userId] || new Date(req.updated_at) > new Date(latestMap[userId].updated_at)) {
        latestMap[userId] = req;
      }
    });
    return latestMap;
  };

  const latestVerifications = getLatestVerificationRequestByUser(verificationRequests || []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOpenModal = (email: string) => {
    setSelectedEmail(email);
    setSelectedVerificationId(undefined);
    setSelectedVerification(undefined);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedEmail(undefined);
    setSelectedVerificationId(undefined);
    setSelectedVerification(undefined);
  };

  const handleVerificationChange = (verificationId: string | null) => {
    setSelectedVerificationId(verificationId || undefined);
    if (verificationId) {
      const verification = verifications.find((v) => v.verification_id === verificationId);
      setSelectedVerification(verification || undefined);
    } else {
      setSelectedVerification(undefined);
    }
  };

  const handleVerificationPress = () => {
    if (selectedEmail && selectedVerificationId) {
      onSendVerificationRequest(selectedEmail, selectedVerificationId);
      handleCloseModal();
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleGroupVerificationPress = () => {
    if (selectedUsers.length === 0) return;
    setGroupVerificationId(undefined);
    setGroupSelectedVerification(undefined);
    setGroupVerificationModalVisible(true);
  };

  const handleGroupVerificationChange = (verificationId: string | null) => {
    setGroupVerificationId(verificationId || undefined);
    if (verificationId) {
      const verification = verifications.find((v) => v.verification_id === verificationId);
      setGroupSelectedVerification(verification || undefined);
    } else {
      setGroupSelectedVerification(undefined);
    }
  };

  const handleGroupVerificationSubmit = () => {
    if (groupVerificationId && selectedUsers.length > 0) {
      const connectionIds: string[] = [];
      selectedUsers.forEach((userId) => {
        const user = users.find((u) => u.user_id === userId);
        if (user) {
          connectionIds.push(user.email);
        }
      });
      onSendGroupVerificationRequest(connectionIds, groupVerificationId);
      setGroupVerificationModalVisible(false);
      setSelectedUsers([]);
    }
  };

  const handleCloseGroupModal = () => {
    setGroupVerificationModalVisible(false);
    setGroupVerificationId(undefined);
    setGroupSelectedVerification(undefined);
  };

  const allSelected = users.every((u) => selectedUsers.includes(u.user_id));

  const handleSelectAllToggle = () => {
    if (allSelected) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u.user_id));
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Users</Text>
        <View style={styles.headerButtons}>
          {selectedUsers.length > 0 && (
            <Text style={styles.selectedCount}>{selectedUsers.length} selected</Text>
          )}

          <GenericButton
            title="Send Group Proof"
            onPress={handleGroupVerificationPress}
            isPrimary={true}
            buttonStyle={styles.createButton}
            isDisabled={selectedUsers.length === 0}
          />
        </View>
      </View>

      <ScrollView>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <View
              style={{
                ...styles.headerCell,
                flex: 0.3,
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <TouchableOpacity onPress={handleSelectAllToggle} style={styles.actionButton}>
                <Text style={styles.actionButtonText}>
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Text>
              </TouchableOpacity>
            </View>
            <Text style={{ ...styles.headerCell, flex: 1 }}>Tenant</Text>
            <Text style={{ ...styles.headerCell, flex: 0.5 }}>Verification</Text>
            <Text style={{ ...styles.headerCell, flex: 0.5 }}>Actions</Text>
          </View>

          {currentUsers.map((user) => {
            const latestRequest = latestVerifications[user.tenant_name];
            return (
              <View key={user.user_id} style={styles.tableRow}>
                <View
                  style={{
                    ...styles.cell,
                    flex: 0.3,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                  <TouchableOpacity
                    style={[
                      styles.checkbox,
                      selectedUsers.includes(user.user_id) && styles.checkboxChecked
                    ]}
                    onPress={() => toggleUserSelection(user.user_id)}>
                    {selectedUsers.includes(user.user_id) && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </TouchableOpacity>
                </View>
                <Text style={{ ...styles.cell, flex: 1, overflow: 'hidden' }}>
                  {user.tenant_name}
                </Text>
                <View
                  style={{ ...styles.cell, flex: 0.5, flexDirection: 'row', alignItems: 'center' }}>
                  {latestRequest?.status === 'done' && latestRequest?.verified === 'true' && (
                    <Tooltip content="Request Verified">
                      <Icon path={mdiCheck} size={1} color="#10B981" />
                    </Tooltip>
                  )}
                  {latestRequest?.status === 'done' && latestRequest?.verified === 'false' && (
                    <Tooltip content="Verification Failed">
                      <Icon path={mdiClose} size={1} color="#EF4444" />
                    </Tooltip>
                  )}

                  {latestRequest?.status === 'abandoned' && (
                    <Tooltip content="Request Rejected">
                      <Icon path={mdiClose} size={1} color="#EF4444" />
                    </Tooltip>
                  )}
                  {latestRequest?.status === 'request-sent' && (
                    <Tooltip content="Request Sent">
                      <Icon path={mdiHelp} size={1} color="#F59E0B" />
                    </Tooltip>
                  )}
                  {!latestRequest && <Text style={{ color: '#9CA3AF' }}></Text>}
                </View>
                <View style={{ ...styles.actionCell, flex: 0.5 }}>
                  <TouchableOpacity
                    onPress={() => handleOpenModal(user.email)}
                    style={styles.actionButton}>
                    <Text style={styles.actionButtonText}>Send Proof</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={handleCloseModal}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Send Proof Request</Text>
            <Text style={styles.modalDescription}>This will send a proof request to the user.</Text>

            <View style={styles.dropdownContainer}>
              <Text style={styles.dropdownLabel}>Select Verification Type:</Text>
              <Picker
                selectedValue={selectedVerificationId}
                style={styles.dropdown}
                onValueChange={(itemValue: string | null) => handleVerificationChange(itemValue)}
                enabled={true}
                mode="dropdown">
                <Picker.Item label="Select a verification type..." value={null} />
                {verifications.map((verification) => {
                  if (verification.status === 'active') {
                    return (
                      <Picker.Item
                        key={verification.verification_id}
                        label={verification.name}
                        value={verification.verification_id}
                      />
                    );
                  }
                  return null;
                })}
              </Picker>

              {selectedVerification && (
                <View style={styles.verificationDetails}>
                  <Text style={styles.verificationDetailsTitle}>Verification Details</Text>
                  <View style={styles.verificationSection}>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>ID:</Text>
                      <Text style={styles.verificationValue}>
                        {selectedVerification.verification_id}
                      </Text>
                    </View>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Name:</Text>
                      <Text style={styles.verificationValue}>{selectedVerification.name}</Text>
                    </View>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Description:</Text>
                      <Text style={styles.verificationValue}>
                        {selectedVerification.description}
                      </Text>
                    </View>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Status:</Text>
                      <Text style={[styles.verificationValue, styles.verificationStatus]}>
                        {selectedVerification.status}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.verificationSection}>
                    <Text style={styles.verificationSubtitle}>Verification Data</Text>
                    {Array.isArray(selectedVerification.data) ? (
                      selectedVerification.data.map((data: IVerificationData) => (
                        <View key={`${data.name}-${data.required}`} style={styles.verificationRow}>
                          <Text style={styles.verificationLabel}>LABEL:</Text>
                          <Text style={styles.verificationValue}>{data.name}</Text>
                          <Text style={styles.verificationLabel}>REQUIRED:</Text>
                          <Text style={styles.verificationValue}>{data.required}</Text>
                        </View>
                      ))
                    ) : (
                      <Text style={styles.verificationValue}>No verification data available</Text>
                    )}
                  </View>

                  <View style={styles.verificationSection}>
                    <Text style={styles.verificationSubtitle}>Timestamps</Text>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Created:</Text>
                      <Text style={styles.verificationValue}>
                        {selectedVerification.created_at}
                      </Text>
                    </View>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Updated:</Text>
                      <Text style={styles.verificationValue}>
                        {selectedVerification.updated_at}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
            </View>

            <TouchableOpacity
              style={[styles.modalButton, !selectedVerificationId && styles.modalButtonDisabled]}
              onPress={handleVerificationPress}>
              <Text style={styles.modalButtonText}>Send Proof Request</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={handleCloseModal}>
              <Text style={styles.modalButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Group Verification Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={groupVerificationModalVisible}
        onRequestClose={handleCloseGroupModal}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Send Group Proof</Text>
            <Text style={styles.modalDescription}>
              This will send a proof request to {selectedUsers.length} selected users.
            </Text>

            <View style={styles.dropdownContainer}>
              <Text style={styles.dropdownLabel}>Select Proof Type:</Text>
              <Picker
                selectedValue={groupVerificationId}
                style={styles.dropdown}
                onValueChange={(itemValue: string | null) =>
                  handleGroupVerificationChange(itemValue)
                }
                enabled={true}
                mode="dropdown">
                <Picker.Item label="Select a proof type..." value={null} />
                {verifications.map((verification) => {
                  if (verification.status === 'active') {
                    return (
                      <Picker.Item
                        key={verification.verification_id}
                        label={verification.name}
                        value={verification.verification_id}
                      />
                    );
                  }
                  return null;
                })}
              </Picker>

              {groupSelectedVerification && (
                <View style={styles.verificationDetails}>
                  <Text style={styles.verificationDetailsTitle}>Proof Details</Text>
                  <View style={styles.verificationSection}>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>ID:</Text>
                      <Text style={styles.verificationValue}>
                        {groupSelectedVerification.verification_id}
                      </Text>
                    </View>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Name:</Text>
                      <Text style={styles.verificationValue}>{groupSelectedVerification.name}</Text>
                    </View>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Description:</Text>
                      <Text style={styles.verificationValue}>
                        {groupSelectedVerification.description}
                      </Text>
                    </View>
                    <View style={styles.verificationRow}>
                      <Text style={styles.verificationLabel}>Status:</Text>
                      <Text style={[styles.verificationValue, styles.verificationStatus]}>
                        {groupSelectedVerification.status}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
            </View>

            <TouchableOpacity
              style={[styles.modalButton, !groupVerificationId && styles.modalButtonDisabled]}
              onPress={handleGroupVerificationSubmit}>
              <Text style={styles.modalButtonText}>Send to Selected Users</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={handleCloseGroupModal}>
              <Text style={styles.modalButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {users.length > 0 && (
        <View style={styles.pagination}>
          <TouchableOpacity
            style={[styles.pageButton, currentPage === 1 && styles.pageButtonDisabled]}
            onPress={() => handlePageChange(currentPage - 1)}>
            <Text
              style={[styles.pageButtonText, currentPage === 1 && styles.pageButtonTextDisabled]}>
              Previous
            </Text>
          </TouchableOpacity>
          <Text style={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </Text>
          <TouchableOpacity
            style={[styles.pageButton, currentPage === totalPages && styles.pageButtonDisabled]}
            onPress={() => handlePageChange(currentPage + 1)}>
            <Text
              style={[
                styles.pageButtonText,
                currentPage === totalPages && styles.pageButtonTextDisabled
              ]}>
              Next
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 16,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3
  },
  createButton: {
    marginLeft: 16
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  selectedCount: {
    marginRight: 10,
    fontSize: 14,
    color: '#4B5563'
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: '#1a1a1a'
  },
  table: {
    width: '100%',
    backgroundColor: '#ffffff'
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    borderBottomWidth: 2,
    borderBottomColor: '#E5E7EB',
    borderRadius: 6,
    marginBottom: 4,
    alignItems: 'center'
  },
  headerCell: {
    flex: 1,
    padding: 12,
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'center'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    backgroundColor: '#ffffff',
    paddingVertical: 4,
    alignItems: 'center'
  },
  cell: {
    flex: 2,
    padding: 12,
    fontSize: 14,
    color: '#4B5563',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center'
  },
  actionCell: {
    flex: 1,
    padding: 12,
    fontSize: 14,
    color: '#374151',
    textAlign: 'center',
    alignItems: 'center'
  },
  actionButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: '#4B5563',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32
  },
  actionButtonText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '500',
    textAlign: 'center'
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 20,
    width: '80%',
    maxWidth: 400,
    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.2)',
    elevation: 5
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
    color: '#1a1a1a'
  },
  modalDescription: {
    fontSize: 14,
    color: '#4B5563',
    marginBottom: 20,
    lineHeight: 20
  },
  dropdownContainer: {
    marginBottom: 20,
    width: '100%'
  },
  dropdownLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8
  },
  dropdown: {
    width: '100%',
    height: 50,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
    backgroundColor: '#F9FAFB',
    color: '#1F2937'
  },
  verificationDetails: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  verificationDetailsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB'
  },
  verificationSection: {
    marginBottom: 20
  },
  verificationSubtitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 12
  },
  verificationRow: {
    flexDirection: 'row',
    marginBottom: 8,
    alignItems: 'flex-start'
  },
  verificationLabel: {
    width: 100,
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
    marginRight: 12
  },
  verificationValue: {
    flex: 1,
    fontSize: 14,
    color: '#1F2937'
  },
  verificationStatus: {
    textTransform: 'capitalize',
    fontWeight: '500'
  },
  modalButton: {
    padding: 12,
    borderRadius: 6,
    backgroundColor: '#4B5563',
    marginBottom: 8
  },
  modalButtonDisabled: {
    backgroundColor: '#9CA3AF',
    opacity: 0.7
  },
  cancelButton: {
    backgroundColor: '#33333F'
  },
  modalButtonText: {
    fontSize: 14,
    color: '#ffffff',
    textAlign: 'center',
    fontWeight: '500'
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    marginTop: 8
  },
  pageButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 6,
    marginHorizontal: 8
  },
  pageButtonDisabled: {
    backgroundColor: '#E5E7EB',
    opacity: 0.5
  },
  pageButtonText: {
    color: '#374151',
    fontWeight: '500'
  },
  pageButtonTextDisabled: {
    color: '#9CA3AF'
  },
  pageInfo: {
    color: '#6B7280',
    marginHorizontal: 16,
    fontSize: 14
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#4B5563',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent'
  },
  checkboxChecked: {
    backgroundColor: '#4B5563'
  },
  checkmark: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold'
  }
});

export default PresTable;
